import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { ReviewCard } from './ReviewCard';
import { StarRating } from './StarRating';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './Button';
import { getProviderReviewsByProviderId } from '../services/api';
import { GetProviderReviewsResponse, ReviewsSummary } from '../types';

interface ProviderReviewsSectionProps {
  providerId: number;
  providerName: string;
  initialSummary?: ReviewsSummary;
}

export const ProviderReviewsSection: React.FC<ProviderReviewsSectionProps> = ({
  providerId,
  providerName,
  initialSummary,
}) => {
  const [reviewsData, setReviewsData] = useState<GetProviderReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const REVIEWS_PER_PAGE = 10;

  useEffect(() => {
    fetchReviews(currentPage);
  }, [providerId, currentPage]);

  const fetchReviews = async (page: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getProviderReviewsByProviderId(providerId, page, REVIEWS_PER_PAGE);

      if (response.success) {
        setReviewsData(response);
      } else {
        setError(response.error || 'Failed to load reviews');
      }
    } catch (err) {
      setError('An error occurred while loading reviews');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    fetchReviews(currentPage);
  };

  const getRatingPercentage = (rating: number, resolvedSummary: typeof initialSummary): number => {
    if (!resolvedSummary?.totalReviews) return 0;
    const count = resolvedSummary.ratingDistribution[rating as 1 | 2 | 3 | 4 | 5] || 0;
    return (count / resolvedSummary.totalReviews) * 100;
  };

  const getRatingColor = (rating: number): string => {
    if (rating === 5) return 'bg-green-500';
    if (rating === 4) return 'bg-green-400';
    if (rating === 3) return 'bg-amber-400';
    if (rating === 2) return 'bg-orange-400';
    return 'bg-red-400';
  };

  if (loading && !reviewsData && !initialSummary) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex justify-center items-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error && !reviewsData && !initialSummary) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Reviews</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={handleRetry} variant="secondary">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const summary = reviewsData?.summary ?? initialSummary;
  const pagination = reviewsData?.pagination;
  const reviews = reviewsData?.reviews ?? initialSummary?.recentReviews ?? [];

  if (!summary || summary.totalReviews === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reviews Yet</h3>
          <p className="text-gray-600">
            Be the first to leave a review for {providerName}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Reviews & Ratings</h2>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <div className="text-5xl font-bold text-gray-900 mb-2">
              {summary.averageRating.toFixed(1)}
            </div>
            <StarRating rating={summary.averageRating} size={24} />
            <p className="text-gray-600 mt-2">
              Based on {summary.totalReviews} {summary.totalReviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const percentage = getRatingPercentage(rating, summary);
              const count = summary.ratingDistribution[rating as 1 | 2 | 3 | 4 | 5] || 0;

              return (
                <div key={rating} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-16">
                    <span className="text-sm font-medium text-gray-700">{rating}</span>
                    <Star size={14} className="fill-amber-400 text-amber-400" />
                  </div>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getRatingColor(rating)} transition-all duration-300`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="p-6">
        {reviews.length > 0 ? (
          <>
            <div className="space-y-4 mb-6">
              {reviews.map((review) => (
                <ReviewCard key={review._id} review={review} />
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages || loading}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No reviews to display</p>
          </div>
        )}
      </div>

      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      )}
    </div>
  );
};
